import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    IPipelineParameters,
    IPipelineSalesFunnel,
} from 'app/modules/pipeline/interfaces/pipeline-data';
import { Observable } from 'rxjs';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { IChartTitleData, IRowItems, ITableTdItems } from 'app/modules/shared/interfaces/card-data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import { Router } from '@angular/router';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import * as milestoneColors from 'app/modules/shared/json/colors/milestone-colors.json';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { ICardlistData } from 'app/modules/shared/components/card-list/card-list.component';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';
@Injectable({
    providedIn: 'root',
})
export class PipelineSalesService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'pipeline';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private _reusableFunctions: ReusableFunctions,
        private _http: HttpClient,
        private _router: Router,
        private _navigationHelper: NavigationHelpService
    ) {}

    getSalesFunnel(parameters: IPipelineParameters): Observable<IPipelineSalesFunnel[]> {
        return this._http.post<IPipelineSalesFunnel[]>(this._url + '/sales/funnel', parameters, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    setSalesPipelineParaMeters(
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        useNonSalesPipeline = false
    ): IPipelineParameters {
        let channel =
            displayOptions.find((item) => item.optionKey === 'channel')?.selectedOptions ?? 'All';
        let loanType =
            displayOptions.find((item) => item.optionKey === 'loanCategory')?.selectedOptions ??
            'All';
        let loanPurpose =
            displayOptions.find((item) => item.optionKey === 'loanPurpose')?.selectedOptions ??
            'All';
        let milestone =
            displayOptions.find((x) => x.optionKey === 'activeMilestones')?.selectedOptions ??
            'All';

        return {
            channel: channel,
            loanType: loanType,
            loanPurpose: loanPurpose,
            hFilterJSON: hFilterJSON,
            milestone: milestone,
            useNonSalesPipeline: useNonSalesPipeline,
        };
    }

    initializeLargeCard(
        largeCardInfo: ICardInfo,
        action: NoDataActionType,
        cardDrilldownUrl: string
    ) {
        let largeCardInteriorOptions = {
            action: action,
            componentTitle: largeCardInfo.name,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: cardDrilldownUrl + '/' + largeCardInfo.urlSegment,
            uniqueId: largeCardInfo.cardId,
            showCardControllers: true,
            queryParams: {},
            height: 630,
        };
        largeCardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    initializeTableCard(largeCardInfo: ICardInfo, action: NoDataActionType) {
        let largeCardTableOptions: ICardlistData = {
            cardId: largeCardInfo.cardId,
            urlSegment: largeCardInfo.urlSegment,
            cardTitle: largeCardInfo.name,
            dataTableData: [],
            hasData: false,
            isDataReady: false,
            isTitleClickable: largeCardInfo.isTitleClickable ?? false,
            searchTerm: '',
            action: action,
            hideViewAll: true,
            showCardControllers: true,
            showPercentages: true,
            //TODO: set these values dynamically within the card-list component
            innerTableHeight: this.isLargerCard() ? '550px' : null,
            outerTableHeight: this.isLargerCard() ? '675px' : null,
        };
        largeCardInfo.cardTableInfo = Object.assign({}, largeCardTableOptions);
    }

    isLargerCard() {
        let url = this._router.url.split('?')[0];
        return (
            url.includes('pipeline/insights/sales-funnel') ||
            url.includes('pipeline/at-a-glance') ||
            url.includes('pipeline/insights/operations-pipeline')
        );
    }

    getPipelineTableProperties() {
        let tableInfo: ITableTdItems[] = [
            {
                headerTitle: 'Milestone',
                fieldId: 'milestone',
                isHyperlinked: true,
                headerStyle: {
                    'text-align': 'left',
                    color: '#333333',
                    'text-transform': 'uppercase',
                    width: '46%',
                },
                style: {
                    'font-weight': '500',
                    'text-align': 'left',
                    'font-size': '14px',
                    width: '46%',
                },
            },
            {
                headerTitle: 'Volume',
                fieldId: 'curVol',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                    width: '27%',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '27%',
                },
            },
            {
                headerTitle: 'Units',
                fieldId: 'curUnits',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                    width: '27%',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '27%',
                },
                showIndicator: true,
                compareFieldId: 'previousMonthlyValue',
                diffField: 'monthlyDiff',
            },
        ];
        return tableInfo;
    }

    constructTable(
        largeCardInfo: ICardInfo,
        data: IPipelineSalesFunnel[],
        action: NoDataActionType,
        useNonSalesPipeline: boolean
    ) {
        let hasData = data && data.length > 0;
        let largeCardTableOptions = largeCardInfo.cardTableInfo;
        largeCardTableOptions.dataTableData = [];

        if (hasData) {
            let tableRowItems: IRowItems[] = [];
            const totalVolume = data.reduce((a, { curVol }) => a + curVol, 0);
            const totalUnits = data.reduce((a, { curUnits }) => a + curUnits, 0);
            const unitPercents = this._reusableFunctions.getRefinedPercents(
                data.map((number) => (number.curUnits / totalUnits) * 100)
            );
            const volumePercents = this._reusableFunctions.getRefinedPercents(
                data.map((number) => (number.curVol / totalVolume) * 100)
            );

            data.forEach((item, i) => {
                let dataItem = Object.assign({}, item);
                let tableTdItems = [];
                let tableInfo = this.getPipelineTableProperties();
                tableInfo.forEach((rowInf) => {
                    let rowInfCopy = Object.assign({}, rowInf);
                    let value = dataItem[rowInfCopy.fieldId];

                    if (rowInfCopy.fieldId == 'curVol') {
                        rowInfCopy.percent = unitPercents[i];
                        value = this._reusableFunctions.formatMoney(value);
                    } else if (rowInfCopy.fieldId == 'curUnits') {
                        rowInfCopy.percent = volumePercents[i];
                        value = this._reusableFunctions.formatUnits(value);
                    }

                    if (rowInfCopy.fieldId == 'milestone') {
                        rowInfCopy.urlSegment = `pipeline-tracker`;
                        rowInfCopy.routerLink = `/reporting/pipeline/`;
                        rowInfCopy.hasAccess = true;
                        rowInfCopy.queryParams = {
                            milestone: value,
                            titleSuffix: ': ' + value,
                            useNonSalesPipeline: useNonSalesPipeline,
                            viewByMilestones: 'Active',
                        };

                        if (i === 0 && largeCardInfo.isTitleClickable) {
                            largeCardTableOptions.queryParams = rowInfCopy.queryParams;
                            largeCardTableOptions.queryParams.milestone = 'All';
                            largeCardTableOptions.queryParams.titleSuffix = '';
                            largeCardTableOptions.routerPath = rowInfCopy.routerLink;
                        }
                    }

                    rowInfCopy.value = value;
                    tableTdItems.push(rowInfCopy);
                });

                tableRowItems.push({
                    tdList: tableTdItems,
                });
            });
            largeCardTableOptions.dataTableData = tableRowItems;
        }

        largeCardTableOptions.action = action;
        largeCardTableOptions.hasData = hasData;
        largeCardTableOptions.isDataReady = true;
        largeCardInfo.cardTableInfo = Object.assign({}, largeCardTableOptions);
    }

    setSalesFunnelData(
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        useNunSalesPipeline: boolean
    ) {
        let params = this.setSalesPipelineParaMeters(
            displayOptions,
            hFilterJson,
            useNunSalesPipeline
        );
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.hasData = false;
        largeCardInteriorOptions.isDataReady = false;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);

        this.getSalesFunnel(params).subscribe(
            (res) => {
                this.constructFunnel(cardInfo, res, action, useNunSalesPipeline);
            },
            (error) => {
                this.constructFunnel(cardInfo, [], NoDataActionType.error, useNunSalesPipeline);
            }
        );
    }

    setTableData(
        cardInfo: ICardInfo,
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        action: NoDataActionType,
        useNunSalesPipeline: boolean
    ) {
        let params = this.setSalesPipelineParaMeters(
            displayOptions,
            hFilterJson,
            useNunSalesPipeline
        );
        let largeCardTableOptions = cardInfo.cardTableInfo;
        largeCardTableOptions.hasData = false;
        largeCardTableOptions.isDataReady = false;
        cardInfo.cardTableInfo = Object.assign({}, largeCardTableOptions);

        this.getSalesFunnel(params).subscribe(
            (res) => {
                this.constructTable(cardInfo, res, action, useNunSalesPipeline);
            },
            (error) => {
                this.constructTable(cardInfo, [], NoDataActionType.error, true);
            }
        );
    }

    constructFunnel(
        cardInfo: ICardInfo,
        data: IPipelineSalesFunnel[],
        action: NoDataActionType,
        useNonSalesPipeline: boolean
    ) {
        let hasData = data && data.length > 0;
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;

        if (hasData) {
            let finalData = [];
            data.forEach((x) => {
                let color = milestoneColors.default.find(
                    (milestone) => milestone?.milestone?.toLowerCase() == x.milestone?.toLowerCase()
                )?.color;
                finalData.push({
                    y: x.curVol,
                    name: x.milestone,
                    properties: x,
                    color: color,
                });
            });

            let sumVol = this._reusableFunctions.calculateSumByProperty(data, 'curVol');
            let sumUnits = this._reusableFunctions.calculateSumByProperty(data, 'curUnits');

            let chartTitleData: IChartTitleData = {
                showChartCustomTitle: true,
                chartTile: cardInfo.name,
                chartLabelColor: '#dededed',
                chartComparisonValue: null,
                chartMainValue: this._reusableFunctions.formatMoney(sumVol),
                chartSecondaryValue: this._reusableFunctions.formatUnits(sumUnits),
                isMainValueUnitVisible: true,
                chartSubItemValue: null,
                isChartSubValueVisible: false,
                svgIconString: '',
            };

            let chartOptions = this.getChartOptions(
                finalData,
                useNonSalesPipeline,
                this.isLargerCard() ? 550 : null
            );
            largeCardInteriorOptions.chartTitleData = chartTitleData;

            setTimeout(() => {
                largeCardInteriorOptions.chart = new Highcharts.Chart(
                    largeCardInteriorOptions.uniqueId,
                    chartOptions
                );
            }, 0);

            largeCardInteriorOptions.showCardControlArea = true;
            largeCardInteriorOptions.queryParams = {
                milestone: 'All',
                useNonSalesPipeline: useNonSalesPipeline,
            };
        }

        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.action = action;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    getChartOptions(data, useNonSalesPipeline: boolean, height: number): Highcharts.Options {
        let _this = this;

        return {
            chart: {
                type: 'funnel',
                height: height, //'630',
                spacingLeft: 50,
                spacingRight: 15,
                marginRight: 100,
            },

            title: {
                text: null,
            },

            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                    },
                },
            },

            legend: {
                enabled: true,
            },

            credits: {
                enabled: false,
            },

            tooltip: {
                backgroundColor: '#FFFFFF',
                borderColor: '#223540',
                borderRadius: 8,
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    padding: '8',
                    textOverflow: 'ellipsis',
                },
                shared: true,
                useHTML: true,
                outside: true,
                formatter: function () {
                    let point = this.point;
                    let tooltip = '<table>';
                    tooltip +=
                        '<tr style="line-height: 18px; font-size: 16px"> <td colSpan="3"> <b>' +
                        point.name.toUpperCase() +
                        '<b></td></tr>';

                    tooltip +=
                        '<tr style="display:' +
                        'block;' +
                        '"><td style="text-align: left;">' +
                        _this._reusableFunctions.formatMoney(point.properties.curVol) +
                        '</td><td style="padding: 0 5px 0 5px">/</td>' +
                        '<td style="text-align: right; padding-left: 20px;">' +
                        point.properties.curUnits.toLocaleString() +
                        '</td></tr>' +
                        '</table>';
                    return tooltip;
                },
                series: [
                    {
                        type: 'funnel',
                        name: 'Volume/Units',
                        data: data,
                    },
                ],
            },
            series: [
                {
                    type: 'funnel',
                    name: 'Volume/Units',
                    point: {
                        events: {
                            click: function () {
                                let queryParams = {
                                    viewByMilestones: 'Active',
                                    milestone: this.properties.milestone,
                                    titleSuffix: ': ' + this.properties.milestone,
                                    useNonSalesPipeline: useNonSalesPipeline,
                                };

                                _this._navigationHelper.navigateTo(
                                    ['/reporting/pipeline/pipeline-tracker'],
                                    queryParams,
                                    true
                                );
                            },
                        },
                    },
                    data: data,
                    dataLabels: {
                        style: {
                            color: '#223540',
                            fontFamily: 'Roboto',
                            padding: '8',
                            fontSize: '12px',
                            width: 150,
                            textOverflow: 'wrap',
                        },
                        useHTML: true,
                        formatter: function () {
                            return (
                                '<div align="center" style="text-align: left; word-wrap: break-word;word-break: break-all; width:150px">' +
                                this.point.name +
                                '</div>'
                            );
                        },
                    },
                },
            ],
            exporting: {
                enabled: false,
                sourceWidth: 800,
                sourceHeight: 600,
            },
        } as Highcharts.Options;
    }
}
